// Alertes (spec § 11) : commentaire ou mise à jour d'un partenaire, action en retard, changement sur une fiche
// suivie. Écrites par la base et le worker ; l'interface ne fait que les afficher et les marquer comme lues.
import { useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AtSign,
  BadgeEuro,
  Bell,
  BellRing,
  Building2,
  CalendarClock,
  FileDiff,
  FileUp,
  Handshake,
  Link2,
  Lock,
  LogOut,
  MessageSquare,
  RadioTower,
  RotateCcw,
  ShieldAlert,
  Share2,
  TrendingDown,
  Unlink,
  Wallet,
} from "lucide-react";
import {
  type Identifier,
  useGetIdentity,
  useGetList,
  useGetMany,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Alert, Contact, Deal, ProjectEvent, Sale } from "../types";
import { nextActionText } from "./NextActionEditor";
import { OfferPage, useOfferOpen } from "./OfferPage";
import { countryName, formatDay } from "./projectUtils";
import { useScreenTip, type TipSpec } from "../onboarding/useScreenTip";
import { WatchSettings } from "./ProjectWatch";
import { SavedSearchList } from "./SavedSearches";
import { subscriptionLabel } from "./territories";
import { useEventText } from "./useEventText";

const UNREAD = { "read_at@is": null };

/** Cloche de l'en-tête : nombre d'alertes non lues, relu chaque minute. */
export const AlertBell = () => {
  const translate = useTranslate();
  const { total } = useGetList<Alert>(
    "alerts",
    {
      filter: UNREAD,
      pagination: { page: 1, perPage: 1 },
      sort: { field: "created_at", order: "DESC" },
    },
    { refetchInterval: 60_000 },
  );
  return (
    <Button
      asChild
      variant="ghost"
      size="icon"
      className="relative rounded-full"
      aria-label={translate("resources.projects.alerts.title")}
    >
      <Link to="/alerts">
        <Bell className="size-5" />
        {!!total && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-signal text-white text-[0.625rem] leading-4 text-center">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </Link>
    </Button>
  );
};

const TIPS: TipSpec[] = [{ target: "alert-settings", key: "alerts.saved" }];

export const AlertList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  // Deux onglets : les alertes reçues, et les réglages (e-mail, sociétés des fiches surveillées, veilles).
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "settings" ? "settings" : "alerts";
  const { data: alerts, isPending } = useGetList<Alert>("alerts", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "created_at", order: "DESC" },
  });
  useScreenTip("tip:alerts", TIPS);
  const unread = alerts?.filter((alert) => !alert.read_at).length ?? 0;

  const markAllRead = async () => {
    // La base limite la mise à jour aux alertes de l'utilisateur et à la seule colonne `read_at`.
    const { error } = await getSupabaseClient()
      .from("alerts")
      .update({ read_at: new Date().toISOString() })
      .is("read_at", null);
    if (error) notify(error.message, { type: "error" });
    queryClient.invalidateQueries({ queryKey: ["alerts"] });
  };

  return (
    <div className="mt-2 pb-4 flex flex-col gap-4 max-w-3xl">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold">
            {translate("resources.projects.alerts.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("resources.projects.alerts.intro")}
          </p>
        </div>
        {unread > 0 && (
          <Button variant="outline" size="sm" onClick={markAllRead}>
            {translate("resources.projects.alerts.mark_all_read")}
          </Button>
        )}
      </div>
      <Tabs
        value={tab}
        onValueChange={(value) =>
          setParams(value === "settings" ? { tab: "settings" } : {}, {
            replace: true,
          })
        }
      >
        <TabsList>
          <TabsTrigger value="alerts">
            {translate("resources.projects.alerts.tab_alerts")}
          </TabsTrigger>
          <TabsTrigger value="settings" data-tour="alert-settings">
            {translate("resources.projects.alerts.tab_settings")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="alerts" className="mt-2">
          {isPending ? null : !alerts?.length ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              {translate("resources.projects.alerts.empty")}
            </p>
          ) : (
            <AlertRows alerts={alerts} />
          )}
        </TabsContent>
        <TabsContent value="settings" className="mt-2 flex flex-col gap-6">
          <EmailSwitch
            id="alert-digest"
            rpc="set_alert_digest"
            field="alert_digest"
            keyPrefix="digest"
          />
          <EmailSwitch
            id="share-emails"
            rpc="set_share_emails"
            field="share_emails"
            keyPrefix="share_emails"
          />
          <WatchSettings />
          <SavedSearchList />
        </TabsContent>
      </Tabs>
    </div>
  );
};

/** Préférence d'e-mail de l'utilisateur, changée par la base : récapitulatif quotidien (`set_alert_digest`) ou
 * e-mail à chaque fiche partagée (`set_share_emails`). */
const EmailSwitch = ({
  id,
  rpc,
  field,
  keyPrefix,
}: {
  id: string;
  rpc: "set_alert_digest" | "set_share_emails";
  field: "alert_digest" | "share_emails";
  keyPrefix: "digest" | "share_emails";
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { data: sale, refetch } = useGetOne<Sale>(
    "sales",
    { id: identity?.id as Identifier },
    { enabled: identity?.id != null },
  );
  if (!sale) return null;

  const change = async (enabled: boolean) => {
    const { error } = await getSupabaseClient().rpc(rpc, { enabled });
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify(
      translate(
        `resources.projects.alerts.${keyPrefix}_${enabled ? "on" : "off"}`,
      ),
    );
    refetch();
  };

  return (
    <div className="flex items-start gap-3">
      <Switch
        id={id}
        checked={!!sale[field]}
        onCheckedChange={change}
        className="mt-0.5"
      />
      <div className="flex flex-col gap-0.5">
        <Label htmlFor={id}>
          {translate(`resources.projects.alerts.${keyPrefix}`)}
        </Label>
        <span className="text-xs text-muted-foreground">
          {translate(`resources.projects.alerts.${keyPrefix}_help`, {
            email: sale.email,
          })}
        </span>
      </div>
    </div>
  );
};

const ids = (values: (number | string | null | undefined)[]) => [
  ...new Set(values.filter((value) => value != null)),
];

const AlertRows = ({ alerts }: { alerts: Alert[] }) => {
  const { data: authors } = useGetMany<Sale>("sales", {
    ids: ids(alerts.map((alert) => alert.author_id)),
  });
  const { data: deals } = useGetMany<Deal>("deals", {
    ids: ids(alerts.map((alert) => alert.deal_id)),
  });
  const { data: contacts } = useGetMany<Contact>("contacts", {
    ids: ids(alerts.map((alert) => alert.contact_id)),
  });
  const { data: events } = useGetMany<ProjectEvent>("project_events", {
    ids: ids(alerts.map((alert) => alert.event_id)),
  });
  return (
    <Card className="py-0">
      <CardContent className="px-0">
        <ul className="divide-y">
          {alerts.map((alert) => (
            <AlertRow
              key={alert.id}
              alert={alert}
              author={authors?.find((sale) => sale.id === alert.author_id)}
              deal={deals?.find((deal) => deal.id === alert.deal_id)}
              contact={contacts?.find(
                (contact) => contact.id === alert.contact_id,
              )}
              event={events?.find((event) => event.id === alert.event_id)}
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const ICONS = {
  partner_note: MessageSquare,
  partner_deal: Handshake,
  project_shared: Share2,
  partnership_new: Link2,
  partnership_ended: Unlink,
  partnership_left: LogOut,
  overdue_action: CalendarClock,
  project_change: FileDiff,
  subscription_request: BadgeEuro,
  saved_search: BellRing,
  silent_source: RadioTower,
  import_due: FileUp,
  import_done: FileUp,
  wrong_data: ShieldAlert,
  source_back: RotateCcw,
  mail_groups: AtSign,
  job_failed: Activity,
  source_drop: TrendingDown,
  saved_search_hidden: Lock,
  weekly_teaser: Lock,
  company_arrived: Building2,
  provider_low: Wallet,
};

type ContactReport = {
  id: number;
  reason: "data" | "unrelated" | "left";
  confirmed_at: string | null;
  undone_at: string | null;
};

/** Suite d'un signalement, pour le responsable des données : retirer la donnée pour toutes les équipes (le
 * signalement n'agit d'abord que sur l'équipe de son auteur), ou annuler le signalement. */
const ReportReview = ({ report }: { report: number }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [row, setRow] = useState<ContactReport | null>(null);
  const [busy, setBusy] = useState(false);
  const t = (key: string) => translate(`resources.projects.alerts.${key}`);
  const load = useCallback(() => {
    getSupabaseClient()
      .from("contact_data_reports")
      .select("id, reason, confirmed_at, undone_at")
      .eq("id", report)
      .maybeSingle()
      .then(({ data }) => setRow(data as ContactReport | null));
  }, [report]);
  useEffect(load, [load]);
  // Le journal n'est lisible que par le responsable des imports : rien pour les autres comptes.
  if (!row) return null;
  const act = async (fn: "confirm_contact_report" | "undo_contact_report") => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc(fn, { report });
    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify(
      `resources.projects.alerts.${fn === "confirm_contact_report" ? "report_confirmed" : "report_undone"}`,
      {
        type: "info",
      },
    );
    load();
  };
  if (row.undone_at) {
    return (
      <p className="px-4 pb-3 pl-11 text-xs text-muted-foreground">
        {t("report_is_undone")}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pl-11">
      {row.reason === "data" &&
        (row.confirmed_at ? (
          <span className="text-xs text-muted-foreground">
            {t("report_is_confirmed")}
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={busy}
            onClick={() => act("confirm_contact_report")}
          >
            {t("report_confirm")}
          </Button>
        ))}
      <Button
        size="sm"
        variant="ghost"
        className="h-7 text-xs"
        disabled={busy}
        onClick={() => act("undo_contact_report")}
      >
        {t("report_undo")}
      </Button>
    </div>
  );
};

const AlertRow = ({
  alert,
  author,
  deal,
  contact,
  event,
}: {
  alert: Alert;
  author?: Sale;
  deal?: Deal;
  contact?: Contact;
  event?: ProjectEvent;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const formatCount = (n: number) => n.toLocaleString(locale ?? "fr");
  const navigate = useNavigate();
  const offerOpen = useOfferOpen();
  const [update] = useUpdate();
  const { dealStages } = useConfigurationContext();
  const { label, change } = useEventText();
  const Icon = ICONS[alert.kind];
  const t = (key: string, options?: object) =>
    translate(`resources.projects.alerts.${key}`, options);
  const stage = (value?: string | null) =>
    dealStages.find((s) => s.value === value)?.label ?? value ?? "";

  const who = author ? `${author.first_name} ${author.last_name}` : "";
  const subject =
    deal?.name ??
    (contact ? `${contact.first_name ?? ""} ${contact.last_name ?? ""}` : "");
  const detail = alert.detail ?? {};
  let title: string;
  let body: string | undefined;
  switch (alert.kind) {
    case "partner_note":
      title = t("partner_note", { who, subject });
      body = detail.text;
      break;
    case "partner_deal":
      title = detail.created
        ? t("partner_follow", { who, subject })
        : detail.old_stage
          ? t("partner_stage", { who, subject, stage: stage(detail.stage) })
          : detail.archived
            ? t("partner_archive", { who, subject })
            : t("partner_update", { who, subject });
      body = nextActionText(
        {
          next_action_type: detail.next_action_type ?? null,
          next_action: detail.next_action ?? null,
        },
        translate,
      )
        ? t("next_action", {
            action: nextActionText(
              {
                next_action_type: detail.next_action_type ?? null,
                next_action: detail.next_action ?? null,
              },
              translate,
            ),
            date: formatDay(detail.next_action_date, locale),
          })
        : undefined;
      break;
    case "project_shared":
      title = t(detail.transmitted ? "project_shared_deal" : "project_shared", {
        who,
        org: detail.org ?? "",
        project: detail.project ?? "",
      });
      break;
    case "partnership_new":
      title = t("partnership_new", { who, org: detail.org ?? "" });
      break;
    case "partnership_ended":
      title = t("partnership_ended", { org: detail.org ?? "" });
      break;
    case "partnership_left":
      // Le lien clos, l'hôte ne lit plus le compte du partenaire : son nom est écrit dans l'alerte.
      title = t("partnership_left", {
        who: who || detail.name || t("a_partner"),
      });
      break;
    case "overdue_action":
      title = t("overdue", { subject });
      body = t("next_action", {
        action: nextActionText(
          {
            next_action_type: detail.next_action_type ?? null,
            next_action: detail.next_action ?? null,
          },
          translate,
        ),
        date: formatDay(alert.due_date, locale),
      });
      break;
    case "subscription_request":
      title = t("subscription_request", { who });
      body = detail.plan
        ? subscriptionLabel(
            {
              plan: detail.plan,
              commitment: detail.commitment ?? "annual",
              country: detail.country,
              zone: detail.zone,
            },
            locale ?? "fr",
            translate,
          )
        : undefined;
      break;
    case "project_change":
      title = t("project_change", { subject });
      body = event ? `${label(event.field)} : ${change(event)}` : undefined;
      break;
    case "company_arrived":
      title = t("company_arrived", {
        subject:
          (locale === "fr" ? detail.name : detail.name_en) ?? detail.name ?? "",
      });
      body = event ? `${label(event.field)} : ${change(event)}` : undefined;
      break;
    case "saved_search":
      title = t("saved_search", { search: detail.search ?? "" });
      body = [
        detail.name,
        detail.country ? countryName(detail.country, locale) : null,
        detail.score != null ? t("score", { score: detail.score }) : null,
      ]
        .filter(Boolean)
        .join(" · ");
      break;
    // Invité (spec § 3, aperçu de la base) : des nombres seulement, l'alerte mène aux offres.
    case "saved_search_hidden":
      title = t("saved_search_hidden", {
        smart_count: detail.count ?? 0,
        count: formatCount(detail.count ?? 0),
        search: detail.search ?? "",
      });
      body = detail.high
        ? t("teaser_high", { count: formatCount(detail.high) })
        : undefined;
      break;
    case "weekly_teaser":
      title = t("weekly_teaser", {
        smart_count: detail.count ?? 0,
        count: formatCount(detail.count ?? 0),
        area: detail.zone
          ? translate(`resources.projects.zones.${detail.zone}`)
          : countryName(detail.country, locale),
      });
      body =
        [
          detail.high
            ? t("teaser_high", { count: formatCount(detail.high) })
            : null,
          detail.tenders
            ? t("teaser_tenders", {
                smart_count: detail.tenders,
                count: formatCount(detail.tenders),
              })
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || undefined;
      break;
    case "silent_source":
      title = t("silent_source", {
        source: detail.source ?? "",
        date: formatDay(alert.due_date, locale),
      });
      body = detail.error
        ? t("last_error", { error: detail.error })
        : undefined;
      break;
    case "source_back":
      title = t("source_back", {
        source: detail.source ?? alert.source_id ?? "",
        date: formatDay(alert.due_date, locale),
      });
      body = t("source_back_body");
      break;
    case "mail_groups":
      title = t("mail_groups", {
        count: detail.count ?? 0,
        words: (detail.words ?? []).join(", "),
      });
      body = t("mail_groups_body");
      break;
    case "job_failed": {
      const errors: string[] = detail.errors ?? [];
      const more = (detail.error_count ?? errors.length) - errors.length;
      title = t(detail.status === "error" ? "job_error" : "job_partial", {
        command: detail.command ?? "",
        date: formatDay(alert.due_date, locale),
      });
      body = [...errors, more > 0 ? t("job_more", { count: more }) : null]
        .filter(Boolean)
        .join(" · ");
      break;
    }
    case "provider_low":
      title = t("provider_low", {
        provider:
          (detail.provider ?? "").charAt(0).toUpperCase() +
          (detail.provider ?? "").slice(1),
        balance: (detail.balance ?? 0).toLocaleString(locale),
        threshold: (detail.threshold ?? 0).toLocaleString(locale),
      });
      body = t("provider_low_body");
      break;
    case "source_drop":
      title = t("source_drop", {
        source: detail.source ?? alert.source_id ?? "",
        date: formatDay(alert.due_date, locale),
      });
      body = t("source_drop_body");
      break;
    case "wrong_data":
      title = t("wrong_data", { who, person: detail.person ?? subject });
      // Site d'une société : retiré de la société, pas d'un contact.
      body = [
        detail.reason === "unrelated" || detail.reason === "left"
          ? t(`wrong_data_${detail.reason}`)
          : t(
              detail.field === "website"
                ? "wrong_site_body"
                : "wrong_data_body",
              {
                data: t(`wrong_data_fields.${detail.field ?? "email"}`),
                value: detail.value ?? "",
              },
            ),
        detail.note ? `« ${detail.note} »` : null,
      ]
        .filter(Boolean)
        .join(" · ");
      break;
    case "import_due": {
      // Le MIRT, le Verkehrsinvestitionsbericht, le ÖBB-Rahmenplan, le Krajowy Program Kolejowy, le budget
      // finlandais, le Standbericht suisse et le budget du SFDI tchèque sont des publications annuelles traitées en ligne de commande, pas des
      // fichiers de l'écran des imports.
      const yearly =
        alert.source_id === "mirt" ||
        alert.source_id === "vib" ||
        alert.source_id === "rahmenplan" ||
        alert.source_id === "kpk" ||
        alert.source_id === "budjetti" ||
        alert.source_id === "standbericht" ||
        alert.source_id === "sfdi";
      title = t(yearly ? "import_due_yearly" : "import_due", {
        source: (alert.source_id ?? "").toUpperCase(),
        period: detail.period ?? "",
      });
      body = t(
        yearly
          ? `import_due_yearly_body_${alert.source_id}`
          : "import_due_body",
        { year: (detail.period ?? "").slice(0, 4) },
      );
      break;
    }
    case "import_done":
      title = t(detail.status === "error" ? "import_error" : "import_done", {
        source: (alert.source_id ?? "").toUpperCase(),
        period: detail.period ?? "",
      });
      body =
        detail.status === "error"
          ? (detail.error ?? undefined)
          : t("import_done_body", {
              kept: detail.result?.kept ?? 0,
              records: detail.result?.records ?? 0,
              stale: detail.result?.stale ?? 0,
            });
      break;
  }

  // Une alerte sur une affaire mène à l'affaire, sauf un changement de la fiche, qui mène à la fiche.
  const target =
    alert.kind === "saved_search_hidden" || alert.kind === "weekly_teaser"
      ? offerOpen
        ? OfferPage.path
        : null
      : alert.kind === "subscription_request"
        ? "/user_territories"
        : alert.kind === "partnership_left"
          ? "/partenaires"
          : alert.kind === "partnership_new" ||
              alert.kind === "partnership_ended"
            ? "/partenariats"
            : alert.kind === "import_due" ||
                alert.kind === "import_done" ||
                alert.kind === "mail_groups"
              ? "/source_imports"
              : alert.kind === "job_failed" ||
                  alert.kind === "source_drop" ||
                  alert.kind === "provider_low"
                ? "/radar_health"
                : (alert.kind === "project_change" ||
                      alert.kind === "company_arrived" ||
                      alert.kind === "project_shared") &&
                    alert.project_id
                  ? `/projects/${alert.project_id}/show`
                  : alert.deal_id
                    ? `/deals/${alert.deal_id}/show`
                    : alert.contact_id
                      ? `/contacts/${alert.contact_id}/show`
                      : alert.project_id
                        ? `/projects/${alert.project_id}/show`
                        : null;

  const open = () => {
    if (!alert.read_at) {
      update("alerts", {
        id: alert.id,
        data: { read_at: new Date().toISOString() },
        previousData: alert,
      });
    }
    if (target) navigate(target);
  };

  return (
    <li>
      <button
        type="button"
        onClick={open}
        className="w-full text-left flex gap-3 px-4 py-3 hover:bg-muted/50"
      >
        <Icon
          className={cn(
            "size-4 mt-0.5 shrink-0",
            alert.kind === "overdue_action"
              ? "text-destructive"
              : "text-muted-foreground",
          )}
        />
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                "text-sm flex-1 min-w-0",
                !alert.read_at && "font-semibold",
              )}
            >
              {title}
            </span>
            {!alert.read_at && <Badge className="shrink-0">{t("new")}</Badge>}
          </div>
          {body && (
            <span className="text-sm text-muted-foreground line-clamp-2">
              {body}
            </span>
          )}
          {target === OfferPage.path && (
            <span className="text-sm font-medium underline underline-offset-2">
              {translate("resources.projects.teaser.offers")}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            {formatDay(alert.created_at, locale)}
          </span>
        </div>
      </button>
      {alert.kind === "wrong_data" && detail.report != null && (
        <ReportReview report={detail.report} />
      )}
    </li>
  );
};
